import { shotsApi } from "@/lib/api";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const COLUMNS = [
  { key: "not_shot", label: "Not Shot", color: "border-zinc-700" },
  { key: "in_progress", label: "In Progress", color: "border-amber-500/40" },
  { key: "completed", label: "Completed", color: "border-emerald-500/40" },
];

export default function ShotBoard({ shots, setShots, onReload }) {
  const grouped = COLUMNS.reduce((acc, c) => {
    acc[c.key] = shots.filter((s) => s.status === c.key);
    return acc;
  }, {});

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const newStatus = result.destination.droppableId;
    const shotId = result.draggableId;
    const shot = shots.find((s) => s.id === shotId);
    if (!shot || shot.status === newStatus) return;
    const updatedLocal = shots.map((s) =>
      s.id === shotId ? { ...s, status: newStatus } : s
    );
    setShots(updatedLocal);
    try {
      await shotsApi.update(shotId, { status: newStatus });
    } catch (e) {
      toast.error("Failed to update status");
      setShots(shots);
    }
  };

  if (shots.length === 0) {
    return (
      <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center text-zinc-500 text-sm">
        Add shots first to use the board.
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => (
          <Droppable droppableId={col.key} key={col.key}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                data-testid={`board-col-${col.key}`}
                className={`rounded-md bg-zinc-900/60 border ${col.color} border-zinc-800 p-3 min-h-[300px] ${
                  snapshot.isDraggingOver ? "bg-zinc-900" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="text-overline text-zinc-400">{col.label}</div>
                  <div className="text-xs font-mono text-zinc-500">
                    {grouped[col.key].length}
                  </div>
                </div>
                <div className="space-y-2">
                  {grouped[col.key].map((shot, idx) => (
                    <Draggable
                      key={shot.id}
                      draggableId={shot.id}
                      index={idx}
                    >
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          data-testid={`board-card-${shot.id}`}
                          className={`p-3 rounded-sm bg-zinc-950 border border-zinc-800 hover:border-amber-500/40 transition-colors cursor-grab active:cursor-grabbing ${
                            snap.isDragging ? "drag-row-dragging" : ""
                          }`}
                          style={prov.draggableProps.style}
                        >
                          <div className="flex items-start gap-2">
                            <GripVertical className="w-3.5 h-3.5 text-zinc-600 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="text-overline text-zinc-500 mb-1">
                                Scene {shot.scene || "—"} · Shot {shot.shot_number || "—"}
                              </div>
                              <div
                                className="text-sm text-zinc-200 line-clamp-3 mb-2"
                                title={shot.description || ""}
                              >
                                {shot.description || "Untitled shot"}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
                                {shot.shot_size && (
                                  <span className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 rounded-sm">
                                    {shot.shot_size}
                                  </span>
                                )}
                                {shot.lens && (
                                  <span className="font-mono">{shot.lens}</span>
                                )}
                                {shot.location && (
                                  <span className="truncate">{shot.location}</span>
                                )}
                              </div>
                            </div>
                            {shot.image_data && (
                              <img
                                src={shot.image_data}
                                alt=""
                                className="w-10 h-10 object-cover rounded-sm border border-zinc-800"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
}
